import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { auth, firestore } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { showLoginToast } from './utils/toastUtils';

// Create context
export const AuthContext = createContext();

// Custom hook for using auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// GuestAwareLink component
export const GuestAwareLink = ({ to, children, className }) => {
    const { canAccess } = useAuth();

    const handleClick = (e) => {
        if (!canAccess(to)) {
            e.preventDefault();
            showLoginToast('Please log in to access this page');
        }
    };

    return (
        <a 
            href={to}
            onClick={handleClick}
            className={className}
        >
            {children}
        </a>
    );
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const lastLoginUpdate = useRef(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                try {
                    // Get the ID token result which contains custom claims
                    const idTokenResult = await currentUser.getIdTokenResult();
                    
                    // Get or create user document in Firestore
                    const userRef = doc(firestore, "users", currentUser.uid);
                    const userDoc = await getDoc(userRef);
                    
                    let userData;
                    if (!userDoc.exists()) {
                        // Create initial user document if it doesn't exist
                        const initialUserData = {
                            email: currentUser.email || 'guest',
                            createdAt: new Date(),
                            lastLogin: new Date(),
                            isAnonymous: currentUser.isAnonymous,
                            role: currentUser.isAnonymous ? 'guest' : 'user'
                        };
                        await setDoc(userRef, initialUserData, { merge: true });
                        userData = initialUserData;
                    } else {
                        userData = userDoc.data();
                        // Only update last login if it's been more than 5 minutes
                        const now = new Date();
                        if (!lastLoginUpdate.current || (now - lastLoginUpdate.current) > 5 * 60 * 1000) {
                            await setDoc(userRef, { 
                                lastLogin: now,
                                isAnonymous: currentUser.isAnonymous,
                                role: currentUser.isAnonymous ? 'guest' : userData.role || 'user'
                            }, { merge: true });
                            lastLoginUpdate.current = now;
                        }
                    }

                    setUser({
                        ...currentUser,
                        isAdmin: idTokenResult.claims.admin === true || userData?.role === 'admin',
                        role: currentUser.isAnonymous ? 'guest' : (userData?.role || 'user'),
                        displayName: userData?.name || currentUser.email || 'Guest User',
                        isAnonymous: currentUser.isAnonymous,
                        isGuest: currentUser.isAnonymous
                    });
                } catch (error) {
                    setUser({
                        ...currentUser,
                        isAdmin: false,
                        role: currentUser.isAnonymous ? 'guest' : 'user',
                        displayName: currentUser.email || 'Guest User',
                        isAnonymous: currentUser.isAnonymous,
                        isGuest: currentUser.isAnonymous
                    });
                }
            } else {
                setUser(null);
            }
            setIsInitializing(false);
        });

        return () => unsubscribe();
    }, []);

    const value = {
        user,
        isInitializing,
        isAdmin: user?.isAdmin === true,
        isGuest: user?.isAnonymous === true,
        canAccess: (path) => {
            if (!user) return false;
            if (user.isAnonymous) {
                // Guest users can only access these paths
                return ['/bingo', '/rules'].includes(path);
            }
            return true;
        }
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
